export type DashboardRole = 'admin' | 'staff';

export interface DashboardUser {
  id: number | string;
  email: string;
  firstName: string;
  lastName: string;
  role: DashboardRole;
  [key: string]: unknown;
}

export interface DashboardSession {
  role: DashboardRole;
  token: string;
  user: DashboardUser;
}

const readStoredUser = (key: string): DashboardUser | null => {
  const raw = localStorage.getItem(key);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as DashboardUser;
  } catch {
    return null;
  }
};

/** Decode JWT payload without verification (just reads the base64 payload) */
const decodeJwtPayload = (token: string): { exp?: number; iat?: number } | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
};

/** Check if a JWT token is expired or will expire within the given buffer (seconds) */
const isTokenExpired = (token: string, bufferSeconds: number = 300): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  const nowInSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowInSeconds + bufferSeconds;
};

export const clearDashboardSessions = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminUser');
  localStorage.removeItem('staffToken');
  localStorage.removeItem('staffUser');
};

export const setAdminSession = (token: string, user: DashboardUser) => {
  clearDashboardSessions();
  localStorage.setItem('adminToken', token);
  localStorage.setItem('adminUser', JSON.stringify(user));
};

export const setStaffSession = (token: string, user: DashboardUser) => {
  clearDashboardSessions();
  localStorage.setItem('staffToken', token);
  localStorage.setItem('staffUser', JSON.stringify(user));
};

export const getActiveDashboardSession = (): DashboardSession | null => {
  const adminToken = localStorage.getItem('adminToken');
  const adminUser = readStoredUser('adminUser');
  if (adminToken && adminUser?.role === 'admin') {
    return {
      role: 'admin',
      token: adminToken,
      user: adminUser,
    };
  }

  const staffToken = localStorage.getItem('staffToken');
  const staffUser = readStoredUser('staffUser');
  if (staffToken && staffUser?.role === 'staff') {
    return {
      role: 'staff',
      token: staffToken,
      user: staffUser,
    };
  }

  return null;
};

export const getDashboardToken = (): string | null => getActiveDashboardSession()?.token || null;

export const getDashboardUser = (): DashboardUser | null => getActiveDashboardSession()?.user || null;

export const isAdminDashboardUser = (): boolean => getActiveDashboardSession()?.role === 'admin';

/** Check if the current session token is expired or about to expire (within 5 min) */
export const isSessionTokenExpired = (): boolean => {
  const token = getDashboardToken();
  if (!token) return false; // no session = not expired, just not logged in
  return isTokenExpired(token, 300); // 5 minute buffer
};

/**
 * Refresh the session token by calling /auth/refresh.
 * Updates localStorage with the new token. Returns true on success.
 */
let refreshInProgress: Promise<boolean> | null = null;

export const refreshSessionToken = async (): Promise<boolean> => {
  // Prevent multiple simultaneous refresh calls
  if (refreshInProgress) return refreshInProgress;

  refreshInProgress = (async () => {
    const session = getActiveDashboardSession();
    if (!session) return false;

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api';
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.token}`,
        },
      });

      if (!response.ok) return false;

      const data = await response.json();
      if (!data.success || !data.token) return false;

      // Update stored token
      if (session.role === 'admin') {
        setAdminSession(data.token, data.user || session.user);
      } else {
        setStaffSession(data.token, data.user || session.user);
      }

      console.log('Session token refreshed successfully');
      return true;
    } catch (e) {
      console.error('Failed to refresh session token:', e);
      return false;
    } finally {
      refreshInProgress = null;
    }
  })();

  return refreshInProgress;
};
